import React, { useState, useCallback, useEffect } from 'react'
import { Contact, Loader2, Upload } from 'lucide-react'
import { handleValidationErrors } from '@/shared/utils'
import { getCampaignConfig } from '@/features/enrollment/core/config/campaigns.config'
import { apiService } from '@/shared/services/api'
import { EnrollmentApi } from '@/features/enrollment'
import { StageWrapper } from '@/features/enrollment/core/StageWrapper'

type Props = {
  enrollmentData: any
  updateEnrollmentData: (field: string, value: any) => void
  handleNavigate: (targetIndex: number) => void
  handleSimpleNavigate?: (targetIndex: number) => void
  handleNavigateWithCompletion?: (targetIndex: number) => Promise<void>
  isNavigating?: boolean
  isCompleting?: boolean
  currentStepIndex?: number
  campaignId?: string
  sessionId?: string
  totalSteps?: number
}

// ✅ UPDATED: Campaign-aware validation
// Note: localFiles parameter is passed from component but not used in validation
// Validation checks enrollmentData which is updated when files are selected
const validateStep6 = (data: any, campaignId?: string) => {
  const errors: { [key: string]: string } = {}
  
  // Get campaign config
  const campaign = getCampaignConfig(campaignId || data.campaignId || data.selectedCampaign)
  
  if (!campaign) {
    console.warn('⚠️ No campaign found for EmergencyNotes validation')
    return errors
  }

  // Check if this is a Roblox campaign
  const isRobloxCampaign = campaignId === 'roblox-exploitation' || 
                            data.selectedCampaign === 'robloxExploitation' ||
                            data.campaign?.name?.toLowerCase().includes('roblox')

  // For Roblox campaigns, validate file uploads
  if (isRobloxCampaign) {
    // Check screenshots/messages (check for array with items, url, name, or isPending)
    const hasScreenshots = 
      (Array.isArray(data.screenshotsMessages) && data.screenshotsMessages.length > 0) ||
      data.screenshotsMessages?.url ||
      data.screenshotsMessages?.name ||
      (Array.isArray(data.screenshotsMessages) && data.screenshotsMessages.some((item: any) => item.isPending))
    
    if (!hasScreenshots) {
      errors.screenshotsMessages = "Screenshots/messages upload is required."
    }
  }

  // Find the EmergencyNotes step for this campaign
  const emergencyStep = campaign.steps.find(s => s.component === 'EmergencyNotes')
  
  // If step is optional, no additional validation needed
  if (emergencyStep?.isOptional && !isRobloxCampaign) {
    return errors
  }
  
  return errors
}

export default function Step6EmergencyNotes({
  enrollmentData,
  updateEnrollmentData,
  handleNavigate,
  handleSimpleNavigate,
  handleNavigateWithCompletion,
  isNavigating,
  isCompleting,
  currentStepIndex,
  campaignId, // ✅ ADD THIS
  sessionId, // ✅ ADD THIS for file uploads
  totalSteps = 10
}: Props) {
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [localFiles, setLocalFiles] = useState<Record<string, File | File[] | null>>({})
  const [uploadingFiles, setUploadingFiles] = useState<Record<string, boolean>>({})
  const [dragOver, setDragOver] = useState<string | null>(null)

  // ✅ Get campaign config to show if step is optional
  const activeCampaignId = campaignId || enrollmentData.campaignId || enrollmentData.selectedCampaign
  const campaign = getCampaignConfig(activeCampaignId)
  const emergencyStep = campaign?.steps.find(s => s.component === 'EmergencyNotes')
  const isOptionalStep = emergencyStep?.isOptional || false

  // Check if this is a Roblox campaign
  const isRobloxCampaign = activeCampaignId === 'roblox-exploitation' || 
                            enrollmentData.selectedCampaign === 'robloxExploitation' ||
                            enrollmentData.campaign?.name?.toLowerCase().includes('roblox')

  // File upload handlers - support multiple files for screenshots
  const handleFileUpload = useCallback((field: string, files: FileList | null) => {
    if (!files || files.length === 0) return

    // Handle multiple files for screenshots/messages
    if (field === 'screenshotsMessages' && files.length > 1) {
      handleMultipleFileUploadLocal(field, files)
      return
    }

    const file = files[0]
    
    // Validate file size
    if (file.size > 10 * 1024 * 1024) {
      setErrors(prev => ({
        ...prev,
        [field]: 'File size must be less than 10 MB'
      }))
      return
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf']
    if (!allowedTypes.includes(file.type)) {
      setErrors(prev => ({
        ...prev,
        [field]: 'File type not supported. Please upload JPEG, PNG, GIF, or PDF files.'
      }))
      return
    }

    // Store file locally
    setLocalFiles(prev => ({ ...prev, [field]: file }))
    
    // Update enrollmentData so validation recognizes the file
    updateEnrollmentData(field, {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified,
      isPending: true
    })
    
    // Clear errors for this field
    setErrors(prev => {
      const newErrors = { ...prev }
      delete newErrors[field]
      return newErrors
    })
  }, [updateEnrollmentData])

  const handleMultipleFileUploadLocal = useCallback((field: string, files: FileList) => {
    // Validate all files
    const validFiles: File[] = []
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      
      // Check file size
      if (file.size > 10 * 1024 * 1024) {
        setErrors(prev => ({
          ...prev,
          [field]: `File "${file.name}" is too large. Maximum size is 10MB.`
        }))
        return
      }

      // Check file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf']
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({
          ...prev,
          [field]: `File "${file.name}" type not supported. Please upload JPEG, PNG, GIF, or PDF files.`
        }))
        return
      }

      validFiles.push(file)
    }

    // Store files locally
    setLocalFiles(prev => ({ ...prev, [field]: validFiles }))
    
    // Update enrollmentData with file metadata
    const fileMetadata = validFiles.map((file, index) => ({
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified,
      index: index + 1,
      isPending: true
    }))
    
    updateEnrollmentData(field, fileMetadata)
    
    // Clear errors
    setErrors(prev => {
      const newErrors = { ...prev }
      delete newErrors[field]
      return newErrors
    })
  }, [updateEnrollmentData])

  const uploadLocalFiles = useCallback(async () => {
    if (!sessionId) return

    const uploadPromises: Promise<any>[] = []

    // Process each field with local files
    Object.entries(localFiles).forEach(([field, fileOrFiles]) => {
      if (!fileOrFiles) return

      const filesArray = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles as File]

      filesArray.forEach((file: File, index: number) => {
        setUploadingFiles(prev => ({ ...prev, [field]: true }))
        
        const formData = new FormData()
        formData.append('document', file)
        formData.append('documentType', field)

        const uploadPromise = apiService.post(`/enrollments/sessions/${sessionId}/roblox/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then(response => {
          return {
            field,
            index,
            file,
            result: response.data // This contains { success: true, data: { documentUrl, fileName, ... } }
          }
        })
        .finally(() => {
          setUploadingFiles(prev => ({ ...prev, [field]: false }))
        })

        uploadPromises.push(uploadPromise)
      })
    })

    if (uploadPromises.length === 0) {
      return
    }

    try {
      const results = await Promise.all(uploadPromises)

      // Group results by field to handle multiple files correctly
      const resultsByField: Record<string, any[]> = {}
      results.forEach(({ field, file, result, index }) => {
        if (!resultsByField[field]) {
          resultsByField[field] = []
        }
        resultsByField[field].push({ file, result, index })
      })

      // Update enrollmentData with uploaded file information
      Object.entries(resultsByField).forEach(([field, fieldResults]) => {
        if (field === 'screenshotsMessages') {
          // Handle multiple files - collect all uploaded files
          const existingData = Array.isArray(enrollmentData.screenshotsMessages) 
            ? [...enrollmentData.screenshotsMessages] 
            : []
          
          // Filter out any pending files (they're being replaced)
          const existingUploadedFiles = existingData.filter((item: any) => !item.isPending)
          
          // Add all newly uploaded files
          // result structure: { success: true, data: { documentUrl, fileName, fileSize, ... } }
          const newFiles = fieldResults.map(({ file, result, index }) => {
            const fileData = result.data || result
            return {
              url: fileData.documentUrl,
              viewUrl: fileData.documentUrl,
              fileName: fileData.fileName,
              fileSize: fileData.fileSize,
              mimeType: file.type,
              uploadedAt: fileData.uploadedAt || new Date().toISOString(),
              name: file.name,
              size: file.size,
              type: file.type,
              lastModified: file.lastModified,
              index: index !== undefined ? index + 1 : existingUploadedFiles.length + 1,
              isPending: false
            }
          })
          
          // Combine existing uploaded files with new files
          const updatedData = [...existingUploadedFiles, ...newFiles]
          
          updateEnrollmentData(field, updatedData)
        }
      })

      // Clear local files after successful upload
      setLocalFiles({})
      
      // Send email with uploaded files
      if (isRobloxCampaign) {
        await sendEmailWithFiles(resultsByField)
      }
    } catch (error) {
      console.error('File upload error:', error)
      setErrors(prev => ({
        ...prev,
        fileUpload: 'Failed to upload files. Please try again.'
      }))
    }
  }, [localFiles, sessionId, updateEnrollmentData, enrollmentData, isRobloxCampaign])

  // Send email with uploaded files to records@alltortsolutions.com
  const sendEmailWithFiles = useCallback(async (resultsByField: Record<string, any[]>) => {
    if (!sessionId) return

    try {
      // Get guardian name and gamer name from enrollment data
      const guardianName = enrollmentData.callerFirstName && enrollmentData.callerLastName
        ? `${enrollmentData.callerFirstName} ${enrollmentData.callerLastName}`
        : enrollmentData.guardianName || 'Guardian'
      
      const gamerName = enrollmentData.injuredPartyFirstName && enrollmentData.injuredPartyLastName
        ? `${enrollmentData.injuredPartyFirstName} ${enrollmentData.injuredPartyLastName}`
        : enrollmentData.gamerName || 'Gamer'

      // Prepare email data
      // Extract file information from resultsByField
      // The result structure is: { success: true, data: { documentUrl, fileName, fileSize, ... } }
      const filesArray = Object.entries(resultsByField).flatMap(([field, fieldResults]) => 
        fieldResults.map(({ result }) => {
          // Handle nested response structure: result.data contains the actual data
          const fileData = result.data || result
          return {
            url: fileData.documentUrl,
            fileName: fileData.fileName,
            fileSize: fileData.fileSize,
            mimeType: fileData.mimeType || 'application/octet-stream'
          }
        })
      )


      const emailData = {
        to: 'records@alltortsolutions.com',
        subject: `Roblox Submission Documents: ${guardianName} OBO ${gamerName}`,
        body: `Records Department, here are the screenshots and proof for the Roblox Tort Campaign.\n\nThis data is for ${guardianName} On Behalf Of ${gamerName}`,
        files: filesArray
      }

      // Call backend API to send email
      await apiService.post(`/enrollments/sessions/${sessionId}/roblox/send-email`, emailData)
    } catch (error) {
      console.error('Failed to send email:', error)
      // Don't block the user if email fails
    }
  }, [sessionId, enrollmentData])

  const handleDragOver = useCallback((e: React.DragEvent, field: string) => {
    e.preventDefault()
    setDragOver(field)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(null)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent, field: string) => {
    e.preventDefault()
    setDragOver(null)
    handleFileUpload(field, e.dataTransfer.files)
  }, [handleFileUpload])

  const refreshScreenshotViewLinks = useCallback(async () => {
    if (!sessionId || !isRobloxCampaign) return

    const screenshots = Array.isArray(enrollmentData.screenshotsMessages)
      ? enrollmentData.screenshotsMessages
      : []

    if (screenshots.length === 0) {
      return
    }

    const requiresRefresh = screenshots.some(
      (file: any) => file?.url && (!file.viewUrl || file.viewUrl === file.url)
    )

    if (!requiresRefresh) {
      return
    }

    try {
      let hasUpdates = false

      const refreshed = await Promise.all(
        screenshots.map(async (file: any) => {
          if (!file?.url) {
            return file
          }

          if (file.viewUrl && file.viewUrl !== file.url) {
            return file
          }

          try {
            const response = await EnrollmentApi.getSignedDocumentUrl(
              sessionId,
              'screenshotsMessages',
              { fileUrl: file.url }
            )

            const signedUrl = response?.signedUrl

            if (!signedUrl || signedUrl === file.viewUrl) {
              return file
            }

            hasUpdates = true

            return {
              ...file,
              viewUrl: signedUrl,
              viewUrlExpiresAt: response?.expiresAt || null
            }
          } catch (error) {
            console.error('Failed to refresh screenshot URL:', error)
            return file
          }
        })
      )

      if (hasUpdates) {
        updateEnrollmentData('screenshotsMessages', refreshed)
      }
    } catch (error) {
      console.error('Failed to refresh screenshot view links:', error)
    }
  }, [sessionId, enrollmentData.screenshotsMessages, updateEnrollmentData, isRobloxCampaign])

  useEffect(() => {
    if (isRobloxCampaign) {
      refreshScreenshotViewLinks()
    }
  }, [isRobloxCampaign, refreshScreenshotViewLinks])

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>, field: string) => {
    handleFileUpload(field, e.target.files)
  }, [handleFileUpload])

  const extractFileNameFromUrl = (url?: string | null) => {
    if (!url) return 'File uploaded'
    try {
      const pathname = new URL(url).pathname
      const fileName = pathname.split('/').pop() || 'File uploaded'
      return decodeURIComponent(fileName.split('?')[0])
    } catch (error) {
      // Fallback to simple parsing if URL constructor fails (relative URLs, etc.)
      const parts = url.split('?')[0].split('/')
      return decodeURIComponent(parts[parts.length - 1] || 'File uploaded')
    }
  }

  // Helper to check if file exists (either uploaded or local)
  const hasFile = (field: string) => {
    return !!(
      localFiles[field] ||
      enrollmentData[field]?.url ||
      enrollmentData[field]?.name ||
      (Array.isArray(enrollmentData[field]) && enrollmentData[field].length > 0)
    )
  }

  // Helper to get file display name
  const getFileDisplayName = (field: string) => {
    if (localFiles[field]) {
      const file = localFiles[field]
      if (Array.isArray(file)) {
        return `${file.length} file(s) selected`
      }
      return (file as File).name
    }
    
    const data = enrollmentData[field]
    if (Array.isArray(data)) {
      return `${data.length} file(s) uploaded`
    }
    
    return data?.name || data?.fileName || extractFileNameFromUrl(data?.url)
  }

  const handleNext = async (): Promise<boolean> => {
    try {
      // ✅ Pass campaign ID to validation
      const validationErrors = validateStep6(enrollmentData, activeCampaignId)
      setErrors(validationErrors)

      if (Object.keys(validationErrors).length === 0) {
        // Upload local files before navigating (if any)
        if (Object.keys(localFiles).length > 0) {
          await uploadLocalFiles()
        }

        return true // Allow navigation
      } else {
        handleValidationErrors(validationErrors)
        return false // Prevent navigation
      }
    } catch (error) {
      console.error('Validation error:', error)
      return false
    }
  }

  const handleBack = async () => {
    setErrors({})
  }

  return (
    <StageWrapper
      currentStepIndex={currentStepIndex ?? 0}
      totalSteps={totalSteps}
      handleNavigate={handleNavigate}
      handleNavigateWithCompletion={handleNavigateWithCompletion}
      onNext={handleNext}
      onBack={handleBack}
      isNavigating={isNavigating}
      isCompleting={isCompleting}
    >
      <div className="card">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center">
            <Contact className="w-6 h-6 text-secondary-600" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-accent-900">
              {isRobloxCampaign ? 'Required Documentation' : 'Emergency Contact & Notes'}
              {/* ✅ Show optional badge */}
              {isOptionalStep && !isRobloxCampaign && (
                <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                  Optional
                </span>
              )}
            </h3>
            <p className="text-sm text-accent-600">
              {isRobloxCampaign 
                ? 'Upload required documents for the Roblox Tort Campaign.'
                : 'Provide emergency contact details (optional) and any additional notes for the legal team.'}
            </p>
          </div>
        </div>

        <div className="space-y-6">
          {isRobloxCampaign ? (
            <>
              {/* Screenshots/Messages Upload - Required for Roblox in STEP 6 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Screen Shots / Messages *
                </label>
                <div
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    dragOver === 'screenshotsMessages' 
                      ? 'border-blue-500 bg-blue-50' 
                      : errors.screenshotsMessages 
                        ? 'border-red-500 bg-red-50' 
                        : hasFile('screenshotsMessages')
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onDragOver={(e) => handleDragOver(e, 'screenshotsMessages')}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, 'screenshotsMessages')}
                >
                  {uploadingFiles.screenshotsMessages ? (
                    <>
                      <Loader2 className="w-8 h-8 mx-auto text-blue-500 mb-2 animate-spin" />
                      <p className="text-sm text-blue-600 mb-2">Uploading...</p>
                    </>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600 mb-2">
                        Drag and drop multiple files here or{' '}
                        <label className="text-blue-600 hover:text-blue-800 cursor-pointer">
                          Browse files
                          <input
                            type="file"
                            className="hidden"
                            accept=".jpg,.jpeg,.png,.pdf"
                            multiple
                            onChange={(e) => handleFileInputChange(e, 'screenshotsMessages')}
                          />
                        </label>
                      </p>
                    </>
                  )}
                  <p className="text-xs text-gray-500">Max file size: 10 MB per file</p>
                  {hasFile('screenshotsMessages') && (
                    <div className="mt-2">
                      {localFiles.screenshotsMessages && Array.isArray(localFiles.screenshotsMessages) ? (
                        <div className="space-y-1">
                          {(localFiles.screenshotsMessages as File[]).map((file, index) => (
                            <p key={index} className="text-sm text-green-600">
                              ✓ {file.name} ({Math.round(file.size / 1024)}KB)
                            </p>
                          ))}
                        </div>
                      ) : Array.isArray(enrollmentData.screenshotsMessages) ? (
                        <div className="space-y-1">
                      {enrollmentData.screenshotsMessages.map((file: any, index: number) => {
                        const displayName = file.fileName || file.name || extractFileNameFromUrl(file.url)
                        const viewHref = file.viewUrl || file.url

                        return (
                          <div key={index} className="text-sm text-green-600 flex items-center space-x-2">
                            <span>✓ {displayName}</span>
                            {viewHref && (
                              <a
                                href={viewHref}
                                target="_blank"
                                rel="noreferrer"
                                className="text-xs text-blue-600 hover:text-blue-800 underline"
                              >
                                View
                              </a>
                            )}
                          </div>
                        )
                      })}
                        </div>
                      ) : (() => {
                        const singleScreenshot = enrollmentData.screenshotsMessages
                        const singleDisplayName = getFileDisplayName('screenshotsMessages')
                        const singleViewHref = singleScreenshot?.viewUrl || singleScreenshot?.url

                        return (
                          <p className="text-sm text-green-600">
                            ✓ {singleDisplayName}
                            {singleViewHref && (
                              <a
                                href={singleViewHref}
                                target="_blank"
                                rel="noreferrer"
                                className="ml-2 text-xs text-blue-600 hover:text-blue-800 underline"
                              >
                                View
                              </a>
                            )}
                          </p>
                        )
                      })()}
                    </div>
                  )}
                </div>
                {errors.screenshotsMessages && (
                  <p className="mt-1 text-sm text-red-600">{errors.screenshotsMessages}</p>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Emergency Contact & Notes - Show for non-Roblox campaigns */}
              {/* Emergency Contact Name */}
              <div>
                <label className="block text-sm font-medium text-accent-900 mb-2">
                  Emergency Contact Name {isOptionalStep && <span className="text-gray-500">(Optional)</span>}
                </label>
                <input 
                  type="text" 
                  name="emergencyContactName"
                  className="input-field w-full" 
                  placeholder="Enter emergency contact name (optional)" 
                  value={enrollmentData.emergencyContactName || ''} 
                  onChange={(e) => updateEnrollmentData('emergencyContactName', e.target.value)} 
                />
              </div>

              {/* Emergency Contact Phone */}
              <div>
                <label className="block text-sm font-medium text-accent-900 mb-2">
                  Emergency Contact Phone # {isOptionalStep && <span className="text-gray-500">(Optional)</span>}
                </label>
              <input 
                type="tel" 
                name="emergencyContactPhone"
                className="input-field w-full" 
                placeholder="Enter emergency contact phone number (optional)" 
                value={enrollmentData.emergencyContactPhone || ''} 
                onChange={(e) => updateEnrollmentData('emergencyContactPhone', e.target.value)} 
              />
              </div>

              {/* Emergency Contact Relationship */}
              <div>
                <label className="block text-sm font-medium text-accent-900 mb-2">
                  Emergency Contact Relationship to client {isOptionalStep && <span className="text-gray-500">(Optional)</span>}
                </label>
                <input 
                  type="text" 
                  className="input-field w-full" 
                  placeholder="e.g., Parent, Spouse, Friend (optional)" 
                  value={enrollmentData.emergencyContactRelationship || ''} 
                  onChange={(e) => updateEnrollmentData('emergencyContactRelationship', e.target.value)} 
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-accent-900 mb-2">
                  Notes {isOptionalStep && <span className="text-gray-500">(Optional)</span>}
                </label>
                <textarea 
                  className="input-field w-full min-h-[100px]" 
                  placeholder="Use this section to let legal teams know if they need to pull medical records, school transcripts, or other records. E.g., 'Claimant went to the doctor', 'Claimant needs school transcripts'." 
                  value={enrollmentData.notes || ''} 
                  onChange={(e) => updateEnrollmentData('notes', e.target.value)}
                />
                <p className="text-xs text-accent-500 mt-1">Optional: Additional information for the legal team</p>
              </div>
          </>
          )}
        </div>
      </div>
    </StageWrapper>
  )
}

